import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import * as authApi from '@/api/auth.api';
import { ApiError } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/hooks/useToast';

/**
 * "Cambiar contraseña" del menú de la cuenta. Pide la actual, la nueva y su
 * repetición. Esta sesión sigue abierta; las de otros equipos se cierran.
 */
export function CambiarClaveModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const [actual, setActual] = useState('');
  const [nueva, setNueva] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cambiar = useMutation({
    mutationFn: () => authApi.changeMyPassword({ currentPassword: actual, newPassword: nueva }),
    onSuccess: () => {
      toast.success('Contraseña cambiada', 'Las sesiones abiertas en otros equipos se cerraron.');
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'No se pudo cambiar la contraseña'),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!actual) return setError('Escriba su contraseña actual');
    if (nueva.length < 8) return setError('La contraseña nueva debe tener al menos 8 caracteres');
    if (nueva !== confirmar) return setError('Las contraseñas nuevas no coinciden');
    cambiar.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Cambiar contraseña"
      size="sm"
      description="Al guardar, se cierran las sesiones abiertas en otros equipos."
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form="cambiar-clave" loading={cambiar.isPending}>
            Guardar
          </Button>
        </>
      }
    >
      <form id="cambiar-clave" onSubmit={submit} className="space-y-4" noValidate>
        <Input
          label="Contraseña actual"
          type="password"
          autoComplete="current-password"
          value={actual}
          onChange={(e) => setActual(e.target.value)}
          autoFocus
        />
        <Input
          label="Contraseña nueva (mínimo 8 caracteres)"
          type="password"
          autoComplete="new-password"
          value={nueva}
          onChange={(e) => setNueva(e.target.value)}
        />
        <Input
          label="Repita la contraseña nueva"
          type="password"
          autoComplete="new-password"
          value={confirmar}
          onChange={(e) => setConfirmar(e.target.value)}
        />
        {error && <p className="text-sm text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
